import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Stack, Button, TextField,
    Grid, Breadcrumbs, Divider, Alert, Tabs, Tab, Switch, MenuItem, FormControlLabel,
    useTheme, useMediaQuery
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import axios from 'axios';
import UserManagement from '../components/Settings/UserManagement';
import { getApiUrl, getUploadUrl } from '../config/api';

const SettingsPage = () => {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [profile, setProfile] = useState({
        name: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        currency: 'USD',
        schoolType: 'PRIMARY' as any,
        logo: null as any,
        smtpHost: '',
        smtpPort: '',
        smtpUser: '',
        smtpPass: '',
        smtpSecure: true
    });
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<any>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Roles allowed to manage users
    const canManageUsers = ['SUPER_ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL'].includes(userRole || '');

    useEffect(() => {
        const userInfo = localStorage.getItem('userInfo');
        if (userInfo) {
            const parsed = JSON.parse(userInfo);
            setUserRole(parsed.role || parsed.user?.role);
        }

        const fetchProfile = async () => {
            try {
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const res = await axios.get(getApiUrl('school'), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setProfile(res.data);
                    if (res.data.logo) {
                        setLogoPreview(getUploadUrl(res.data.logo));
                    }
                }
            } catch (error) {
                console.error('Fetch profile error:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleSave = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            const formData = new FormData();
            Object.keys(profile).forEach(key => {
                const value = (profile as any)[key];
                if (key === 'logo' && value instanceof File) {
                    formData.append('logo', value);
                } else if (key !== 'logo' && value !== null) {
                    formData.append(key, value);
                }
            });

            await axios.put(getApiUrl('school'), formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setStatus({ type: 'success', message: 'School profile updated successfully!' });
        } catch (error: any) {
            setStatus({ type: 'error', message: error.response?.data?.message || 'Update failed' });
        }
    };

    if (loading) return null;

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={2} spacing={isMobile ? 1 : 0}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C">Settings</Typography>
                    <Typography variant="body2" color="text.secondary">Configure school profile and system preferences</Typography>
                </Box>
                {!isMobile && (
                    <Breadcrumbs separator="/" aria-label="breadcrumb">
                        <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>System</Typography>
                        <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Settings</Typography>
                    </Breadcrumbs>
                )}
            </Stack>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={(_, v) => setTabValue(v)}
                    textColor="primary"
                    indicatorColor="primary"
                    variant={isSmallMobile ? "scrollable" : "standard"}
                    scrollButtons="auto"
                >
                    <Tab label="School Profile" />
                    {canManageUsers && <Tab label="User Management" />}
                    {userRole === 'SUPER_ADMIN' && <Tab label="Role Permissions" />}
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, lg: 8 }}>
                        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: isSmallMobile ? 2 : 4 }}>
                            <Typography variant="h6" fontWeight={700} mb={3}>School Profile</Typography>

                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={12} sx={{ display: 'flex', flexDirection: isSmallMobile ? 'column' : 'row', alignItems: isSmallMobile ? 'flex-start' : 'center', gap: 3 }}>
                                    <Box
                                        sx={{
                                            width: 100, height: 100,
                                            borderRadius: '50%',
                                            bgcolor: '#f0f0f0',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', border: '2px solid #e0e0e0',
                                            flexShrink: 0
                                        }}
                                    >
                                        {logoPreview ? (
                                            <img src={logoPreview} alt="School Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">NO LOGO</Typography>
                                        )}
                                    </Box>
                                    <Box>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            size="small"
                                            sx={{ textTransform: 'none', mb: 1, borderColor: '#263B7C', color: '#263B7C' }}
                                        >
                                            Upload Logo
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/png, image/jpeg"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        setProfile({ ...profile, logo: file });
                                                        setLogoPreview(URL.createObjectURL(file));
                                                    }
                                                }}
                                            />
                                        </Button>
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Max 3MB. PNG or JPG only.
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {status && <Alert severity={status.type} sx={{ mb: 3 }}>{status.message}</Alert>}

                            <Stack spacing={3}>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <TextField
                                            fullWidth label="School Name"
                                            value={profile.name}
                                            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth label="Email Address"
                                            value={profile.email}
                                            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth label="Phone Number"
                                            value={profile.phone}
                                            onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            fullWidth label="School Website"
                                            value={(profile as any).website || ''}
                                            onChange={(e) => setProfile({ ...profile, website: e.target.value } as any)}
                                            placeholder="https://www.mainwayinternational.com"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextField
                                            fullWidth multiline rows={3} label="Physical Address"
                                            value={profile.address}
                                            onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth label="Accounting Currency"
                                            value={profile.currency}
                                            onChange={(e) => setProfile({ ...profile, currency: e.target.value })}
                                            helperText="Default currency for all financial transactions"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth select label="School Type"
                                            value={profile.schoolType || 'PRIMARY'}
                                            onChange={(e) => setProfile({ ...profile, schoolType: e.target.value })}
                                            helperText="Determines grades and default service configurations"
                                        >
                                            <MenuItem value="PRE_SCHOOL">Pre-School (Infancy - ECD B)</MenuItem>
                                            <MenuItem value="PRIMARY">Primary School (ECD A - Grade 7)</MenuItem>
                                            <MenuItem value="HIGH_SCHOOL">High School (Form 1 - Form 6)</MenuItem>
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <Divider>
                                    <Typography variant="overline" color="text.secondary" fontWeight={700}>Email Server (SMTP)</Typography>
                                </Divider>

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 8 }}>
                                        <TextField
                                            fullWidth label="SMTP Host"
                                            value={(profile as any).smtpHost || ''}
                                            onChange={(e) => setProfile({ ...profile, smtpHost: e.target.value } as any)}
                                            placeholder="e.g. smtp.gmail.com"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4 }}>
                                        <TextField
                                            fullWidth label="SMTP Port"
                                            type="number"
                                            value={(profile as any).smtpPort || ''}
                                            onChange={(e) => setProfile({ ...profile, smtpPort: e.target.value } as any)}
                                            placeholder="465 or 587"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth label="SMTP Username"
                                            value={(profile as any).smtpUser || ''}
                                            onChange={(e) => setProfile({ ...profile, smtpUser: e.target.value } as any)}
                                            autoComplete="off"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            fullWidth label="SMTP Password"
                                            type="password"
                                            value={(profile as any).smtpPass || ''}
                                            onChange={(e) => setProfile({ ...profile, smtpPass: e.target.value } as any)}
                                            autoComplete="new-password"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={(profile as any).smtpSecure || false}
                                                    onChange={(e) => setProfile({ ...profile, smtpSecure: e.target.checked } as any)}
                                                />
                                            }
                                            label="Use SSL/TLS (Secure Connection)"
                                        />
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Enable this for port 465. If using port 587 (STARTTLS), you may need to disable this.
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Divider />

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        fullWidth={isSmallMobile}
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSave}
                                        sx={{ bgcolor: '#263B7C', borderRadius: '8px', px: 4 }}
                                    >
                                        SAVE SETTINGS
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, lg: 4 }}>
                        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: isSmallMobile ? 2 : 4 }}>
                            <Typography variant="h6" fontWeight={700} mb={3}>Accounting Standards</Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                The system is currently operating on **Double-Entry Bookkeeping** (IFRS compliant).
                            </Typography>
                            <Stack spacing={1}>
                                <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 700 }}>✓ Automatic Balanced Ledger Posting</Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 700 }}>✓ Partial Payment Logic Active</Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 700 }}>✓ Real-time Statement Generation</Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {tabValue === 1 && canManageUsers && (
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: isSmallMobile ? 2 : 4 }}>
                    <UserManagement />
                </Paper>
            )}

            {tabValue === 2 && userRole === 'SUPER_ADMIN' && (
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: isSmallMobile ? 2 : 4 }}>
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" fontWeight={700} mb={1}>Access Control Matrix</Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            Manage granular permissions and feature access for all system roles.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/dashboard/permissions')}
                            sx={{ bgcolor: '#263B7C', borderRadius: '8px' }}
                        >
                            Open Permissions Manager
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default SettingsPage;
